// @flow

import React from 'react';
import bemify, { toModifiers } from 'bemify-js';

import { StaticImage } from '@zapier/common-components';

import Button from 'app/common/components/Button';
import Icon from 'app/common/components/Icon';
import Link from 'app/common/components/Link';
import PageSection from 'app/common/components/PageSection';
import { HeldReasons, REPLAY_STEP } from 'app/history/utils/constants';
import 'app/dashboard/components/HeldTasksBanner.scss';

import type { ActionableHeldTasksProps } from 'app/dashboard/utils/HeldTaskBannerUtils';

const bem = bemify('held-tasks-banner');

type StepItemProps = {
  cta: string,
  ctaUrl: ?string,
  description: string,
  isDone: boolean,
  numOfStepsDone: number,
  onReplayHeldTasks?: () => void,
};

const StepItem = ({
  cta,
  ctaUrl,
  description,
  isDone,
  numOfStepsDone,
  onReplayHeldTasks = () => {},
}: StepItemProps) => (
  <li
    className={bem(
      '__step',
      toModifiers({ done: isDone, indented: numOfStepsDone > 0 || isDone })
    )}
  >
    {isDone && (
      <Icon className={bem('__icon', toModifiers('before'))} type="check" />
    )}
    <Link href={ctaUrl} onClick={onReplayHeldTasks}>
      {cta}
    </Link>
    &nbsp;
    <span className={bem('__step-description')}>{description}</span>
  </li>
);

type Props = {
  title: string,
  daysRemainText: string,
  msg: string,
  buttonText: string,
  isOpen: boolean,
  kind?: 'accommodate-sidebar',
  stepsPropsList: ActionableHeldTasksProps[],
  numOfStepsDone: number,
  onClick: () => void,
  onReplayHeldTasks: () => void,
};

const HeldTasksBanner = ({
  title,
  daysRemainText,
  msg,
  buttonText,
  isOpen,
  kind,
  stepsPropsList,
  numOfStepsDone,
  onClick,
  onReplayHeldTasks,
}: Props) => {
  const listOfSteps = stepsPropsList.map(stepProps => {
    const props =
      stepProps.key === REPLAY_STEP || stepProps.key === HeldReasons.TOO_MANY
        ? {
            ...stepProps,
            numOfStepsDone,
            onReplayHeldTasks,
          }
        : {
            ...stepProps,
            numOfStepsDone,
          };
    return <StepItem {...props} />;
  });

  return (
    <div className={bem('__breakout', toModifiers(kind))}>
      <PageSection kind="padded-horz">
        <div className={bem('__row')}>
          <div className={bem('__left-col')}>
            <StaticImage
              alt="Zapling"
              className={bem('__main-icon')}
              src="images/zaplings/zapling-held.svg"
            />
            <div className={bem('__info')}>
              <div className={bem('__title')}>
                <span className={bem('__tittle-text')}>{title}</span>
                <span className={bem('__message-wrapper')}>
                  <Icon className={bem('__icon')} type="clock" />
                  <span className={bem('__message-outer')}>
                    <span>
                      {daysRemainText}
                      &nbsp;
                    </span>
                    <span className={bem('__message')}>{msg}</span>
                  </span>
                </span>
              </div>
            </div>
          </div>
          {isOpen ? (
            <button className={bem('__arrow-icon-btn')} onClick={onClick}>
              <Icon className={bem('__arrow-icon')} type="arrow-up" />
            </button>
          ) : (
            <Button
              className={bem('__button')}
              kind="warning-banner-cta xlarge"
              onClick={onClick}
            >
              {buttonText}
            </Button>
          )}
        </div>
        {isOpen && stepsPropsList && stepsPropsList.length > 0 && (
          <div
            className={bem('__row', toModifiers('moved-up', 'no-padding-top'))}
          >
            <div className={bem('__info', toModifiers('indented'))}>
              <ol className={bem('__steps')}>{listOfSteps}</ol>
            </div>
          </div>
        )}
      </PageSection>
    </div>
  );
};

export default HeldTasksBanner;
